import { useEffect, type FC } from 'react';
import { useParams, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Spin, Tabs } from 'antd';
import { Space } from '@g2u/ui-kit';

import { ServiceDetails, ServicePayment } from '.';
import { useJob } from 'helpers';
import { RecordLockType, Service } from 'types';
import { useCheckLock } from 'hooks/useCheckLock';
import { Content } from 'components';
import { RootStore } from 'store';
import { jobTabs } from 'data';
import styles from './edit.module.scss';
import { CustomerHeadingRibbon } from 'components/common/customer-heading-ribbon';
import { ServiceSummary } from './service-summary';
import { JobTickets, Actions } from './components';
import { MessageList } from 'components/common/message-list';
import { ServiceSMS } from './service-sms';

export const JobEditPage: FC = observer(() => {
  const { id, tab } = useParams();
  const { setBreadcrumbs } = RootStore;
  const navigate = useNavigate();
  const { isLocked } = useCheckLock({ recordId: id ? Number(id) : undefined, recordLockType: RecordLockType.Job });

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/job/search',
        title: 'Find a job',
      },
      {
        title: `Job ${id}`,
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, []);

  const { job, isLoading: isJobLoading } = useJob(id);

  const handleChangeTab = (tabId: string) => {
    navigate(`/job/edit/${id}/${tabId}`);
  };

  const mapTabs = (tabId: string) => {
    switch (tabId) {
      case 'service-details':
        return <ServiceDetails jobId={id} isLocked={isLocked || job?.status === 'completed'} />;
      case 'service-payment':
        return <ServicePayment jobId={id} isLocked={isLocked} />;
      case 'summary':
        return <ServiceSummary jobId={id} isLocked={isLocked} />;
      case 'tickets':
        return <JobTickets jobId={id} />;
      case 'emails':
        return <MessageList for="job" id={id || ''} messageType="emails" />;
      case 'sms':
        return <ServiceSMS job={job as Service} />;
      default:
        return;
    }
  };

  return (
    <Content dataTestId="service-edit-page" className={styles.jobEdit}>
      <title>Edit Job</title>
      <CustomerHeadingRibbon customer={job?.customer}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large} className={styles.jobEdit__container}>
          <Tabs
            onChange={handleChangeTab}
            activeKey={tab}
            defaultActiveKey={tab ?? 'summary'}
            destroyInactiveTabPane
            tabPosition="top"
            style={{ minHeight: 220 }}
            items={jobTabs.map((tab) => {
              return {
                label: tab.title,
                key: tab.id,
                children:
                  !job || isJobLoading ? (
                    <Space.$ justify={Space.Justify.Center}>
                      <Spin style={{ margin: '0 auto', padding: 40 }} />
                    </Space.$>
                  ) : (
                    mapTabs(tab.id)
                  ),
              };
            })}
          />
        </Space.$>
      </CustomerHeadingRibbon>
      <Actions job={job} />
    </Content>
  );
});
